// EventCard.js
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Button from './ui/Button';

const EventCard = ({ event }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const isUserParticipating = () => {
    if (!user || !event.participants) return false;
    return event.participants.some(p => p.userId === user.uid);
  };

  const isPastEvent = () => {
    if (!event.date) return false;
    return new Date(event.date) < new Date();
  };

  const handleViewDetails = () => {
    navigate(`/events/${event.id}`);
  };

  return (
    <div className="event-card">
      {event.imageUrl && (
        <div className="event-image">
          <img src={event.imageUrl} alt={event.title} />
          {event.category && (
            <div className="event-category-overlay">{event.category}</div>
          )}
        </div>
      )}

      {!event.imageUrl && event.category && (
        <div className="event-category">{event.category}</div>
      )}
      
      <div className="event-content">
        <h3 className="event-title">{event?.title || 'Event Title'}</h3>
        
        <p className="event-description">
          {event?.description 
            ? (event.description.length > 120 
                ? event.description.substring(0, 120) + '...' 
                : event.description)
            : 'Event description'}
        </p>
      
      <div className="event-meta">
        <div className="meta-item">
          <span className="meta-icon">📅</span>
          <span>{formatDate(event?.date)}</span>
        </div>
        
        {event.location && (
          <div className="meta-item">
            <span className="meta-icon">📍</span>
            <span>{event.location}</span>
          </div>
        )}
        
        {event.participants && (
          <div className="meta-item">
            <span className="meta-icon">👥</span>
            <span>{event.participants.length} participants</span>
          </div>
        )}

        {event.points && (
          <div className="meta-item">
            <span className="meta-icon">⭐</span>
            <span>{event.points} points</span>
          </div>
        )}
      </div>
      </div>

      {event.participationEnabled === false && (
        <div className="participation-disabled-notice">
          ⚠️ Participation currently disabled
        </div>
      )}

      <div className="event-footer">
        {isUserParticipating() && (
          <div className="participation-badge">
            ✓ Registered
          </div>
        )}
        {isPastEvent() && (
          <div className="past-badge">
            Past Event
          </div>
        )}
        <Button onClick={handleViewDetails} className="view-details-btn">
          View Details
        </Button>
      </div>

      <style jsx>{`
        .event-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s, box-shadow 0.2s;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .event-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }

        .event-image {
          position: relative;
          width: 100%;
          height: 200px;
          overflow: hidden;
          background: #f0f0f0;
        }

        .event-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .event-card:hover .event-image img {
          transform: scale(1.05);
        }

        .event-category-overlay {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.95);
          color: #1976d2;
          padding: 0.35rem 0.85rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .event-content {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          flex-grow: 1;
        }

        .event-category {
          display: inline-block;
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 600;
          align-self: flex-start;
          margin: 1.5rem 1.5rem 0 1.5rem;
        }

        .event-title {
          margin: 0;
          color: #1a1a1a;
          font-size: 1.25rem;
          line-height: 1.4;
        }

        .event-description {
          color: #666;
          margin: 0;
          line-height: 1.6;
          flex-grow: 1;
        }

        .event-meta {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #555;
          font-size: 0.9rem;
        }

        .meta-icon {
          font-size: 1rem;
        }

        .event-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          margin-top: auto;
          padding: 1rem 1.5rem;
          border-top: 1px solid #e0e0e0;
        }

        .participation-disabled-notice {
          background: #fff3cd;
          color: #856404;
          padding: 0.75rem 1.5rem;
          font-size: 0.9rem;
          font-weight: 500;
          text-align: center;
          border-top: 1px solid #ffeaa7;
        }

        .participation-badge {
          background: #4caf50;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .past-badge {
          background: #9e9e9e;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .view-details-btn {
          margin-left: auto;
        }

        @media (max-width: 768px) {
          .event-image {
            height: 180px;
          }

          .event-content {
            padding: 1rem;
          }

          .event-category {
            margin: 1rem 1rem 0 1rem;
          }

          .event-footer {
            flex-direction: column;
            align-items: stretch;
            padding: 1rem;
          }

          .view-details-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default EventCard;
